/**
 * Accessibilita' dei documenti — che cosa non si legge, e come si rimedia.
 *
 * Perche' e' una pagina e non una colonna
 * ---------------------------------------
 * La colonna c'e', in «Documenti e cartelle», e non basta: quella pagina mostra
 * un obbligo per volta, mentre i documenti da correggere sono sparsi fra
 * obblighi diversi. Chi deve rimediare non parte da un obbligo — parte dal
 * problema, e ha bisogno di vedere in un posto solo tutto cio' che non va.
 *
 * Che cosa si vede, e da chi viene
 * --------------------------------
 * Ogni riga dichiara **chi ha espresso il verdetto**: il validatore ufficiale
 * veraPDF, oppure l'analisi strutturale interna, che vede molto meno e il cui
 * silenzio non e' un'assoluzione. E' la stessa ragione per cui esistono due
 * stati distinti: `verificata` la produce solo veraPDF, `presunta` («forse non
 * accessibile») dice che il documento dichiara cio' che serve senza che
 * nessuno l'abbia certificato. Fonderli renderebbe il segno di spunta una bugia.
 *
 * E ogni riga dice **per che cosa** il documento non va — «Lingua del documento
 * non dichiarata», una voce per requisito — non solo che qualcosa non va. Le
 * voci le scrive il server, e questa pagina le mostra senza inventarne di
 * proprie. Il rimedio sta nella legenda, uno per stato: in una colonna, una
 * procedura per riga rendeva l'elenco illeggibile.
 *
 * Che cosa questa pagina non fa
 * -----------------------------
 * Non spubblica e non blocca. Un documento non accessibile resta pubblicato —
 * deve restarlo cinque anni, art. 8 c. 3 — e il rimedio e' «Sostituisci»: il
 * file corretto prende il posto del vecchio nella stessa pubblicazione, con lo
 * stesso titolo, e resta in bozza finche' qualcuno non preme «Pubblica». Alla
 * pubblicazione l'alias `-latest` si sposta sul nuovo, e il vecchio indirizzo
 * continua a rispondere.
 */

import { useCallback, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  elencaDocumenti,
  ErroreApi,
  riverificaDocumento,
  sostituisciDocumento,
} from "../api/client";
import { STATI_ACCESSIBILITA } from "../api/types";
import type {
  Documento,
  DocumentoTrovato,
  ElencoDocumenti,
  StatoAccessibilita,
} from "../api/types";
import { useAutenticazione } from "../auth/contesto";
import { EsitoAccessibilita } from "../components/EsitoAccessibilita";
import { LegendaAccessibilita } from "../components/LegendaAccessibilita";
import { ModaleCaricamento } from "../components/ModaleCaricamento";
import {
  Avviso,
  Bottone,
  Chip,
  ChipAccessibilita,
  ChipNodo,
  Scheda,
  Scheletro,
  StatoVuoto,
} from "../components/primitive";
import { useAlberatura } from "../dati/contesto-alberatura";
import {
  DESCRIZIONE_ACCESSIBILITA,
  ETICHETTA_ACCESSIBILITA,
  STATI_DA_CORREGGERE,
  TONO_ACCESSIBILITA,
  esitoRiverifica,
  etichettaTitolo,
  fonteLeggibile,
  mancanzeDi,
  raggruppaPerNodo,
  ricercaSenzaOggetto,
  statoDi,
  testoErroriRilevati,
} from "../lib/accessibilita";
import { catenePerId, partiPercorso, SEPARATORE_PERCORSO } from "../lib/alberatura";
import { testoCopie } from "../lib/copie";
import { leggiScadenzaFile } from "../lib/scadenze";
import { useRisorsa } from "../lib/useRisorsa";

/** Quanti documenti per richiesta. Il tetto dell'API e' 100. */
const PER_PAGINA = 100;

/**
 * Stati offerti come filtro, nell'ordine in cui interessano.
 *
 * `non_applicabile` non c'e': filtrare per «nessun controllo da eseguire» non e'
 * una domanda che qualcuno si pone, e offrirla suggerirebbe che JSON e CSV
 * siano documenti carenti — mentre sono il formato che la specifica preferisce.
 */
const STATI_FILTRABILI = STATI_ACCESSIBILITA.filter(
  (stato) => stato !== "non_applicabile",
);

/**
 * I contatori, nell'ordine dell'urgenza, dal rosso al verde. Titolo e tono
 * vengono da `lib/accessibilita` (`etichettaTitolo`, `TONO_ACCESSIBILITA`): le
 * stesse parole e gli stessi colori dei chip, non un terzo modo di dire la
 * stessa cosa.
 */
const CONTATORI: readonly StatoAccessibilita[] = [
  "non_verificata",
  "non_garantita",
  "presunta",
  "verificata",
];

export function Accessibilita() {
  const { ente, nodi } = useAlberatura();
  const { eSuperAdmin } = useAutenticazione();
  const enteId = ente?.id ?? null;

  const [statiScelti, setStatiScelti] = useState<StatoAccessibilita[]>([
    ...STATI_DA_CORREGGERE,
  ]);
  const [testo, setTesto] = useState("");
  const [cercato, setCercato] = useState("");
  const [inRiverifica, setInRiverifica] = useState<number | null>(null);
  const [erroreOperazione, setErroreOperazione] = useState<string | null>(null);
  const [esito, setEsito] = useState<ReactNode>(null);
  const [daSostituire, setDaSostituire] = useState<DocumentoTrovato | null>(null);
  /**
   * Documenti gia' sostituiti in questa visita, con il loro sostituto.
   *
   * L'elenco mostra cio' che e' pubblicato, quindi il documento vecchio resta
   * in tabella finche' la bozza non viene pubblicata. Senza un segno sulla
   * riga, chi ha appena sostituito un file lo ritroverebbe identico e
   * penserebbe che la sostituzione non sia avvenuta.
   */
  const [sostituiti, setSostituiti] = useState<ReadonlyMap<number, Documento>>(new Map());

  const catene = useMemo(() => catenePerId(nodi), [nodi]);

  const chiaveStati = statiScelti.join(",");
  const carica = useCallback(
    (segnale: AbortSignal) =>
      // La regola sta in `lib/accessibilita`, dove le verifiche la raggiungono.
      ricercaSenzaOggetto({ stati: statiScelti, enteId, eSuperAdmin })
        ? Promise.resolve<ElencoDocumenti>({ documenti: [], totale: 0, conteggi: {} })
        : elencaDocumenti(
            {
              q: cercato,
              accessibilita: statiScelti,
              limite: PER_PAGINA,
              ente: enteId,
            },
            segnale,
          ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enteId, cercato, chiaveStati, eSuperAdmin],
  );
  const risorsa = useRisorsa<ElencoDocumenti>(carica, [
    enteId,
    cercato,
    chiaveStati,
  ]);

  const elenco = risorsa.dato;
  const documenti = useMemo(() => elenco?.documenti ?? [], [elenco]);
  const gruppi = useMemo(() => raggruppaPerNodo(documenti), [documenti]);

  // I conteggi sono quelli dell'API: valgono sull'intero filtro e non sulla
  // pagina. Contarli qui direbbe «3 da correggere» a chi ne ha dodici, cioe'
  // mentirebbe proprio a chi sta filtrando per correggerli.
  const conteggi = elenco?.conteggi ?? {};

  function commuta(stato: StatoAccessibilita) {
    setStatiScelti((correnti) =>
      correnti.includes(stato)
        ? correnti.filter((voce) => voce !== stato)
        : [...correnti, stato],
    );
  }

  function sostituito(vecchio: DocumentoTrovato, nuovo: Documento, ancheCopie: boolean) {
    setDaSostituire(null);
    setErroreOperazione(null);
    setSostituiti((correnti) => new Map(correnti).set(vecchio.id, nuovo));
    const idNodo = vecchio.nodo?.id_nodo;
    setEsito(
      <>
        Sostituto di «{vecchio.titolo}» caricato in bozza. Non e' ancora sul
        portale: diventa pubblico con «Pubblica»
        {idNodo ? (
          <>
            , in <Link to={`/documenti?nodo=${encodeURIComponent(idNodo)}`}>Documenti e cartelle</Link>
          </>
        ) : null}
        . Fino ad allora qui resta la versione pubblicata.
        {/* Dalla scelta fatta nella modale, non dallo stato delle copie: una
            copia puo' essere gia' in bozza per conto suo, e dire «sostituito
            anche li'» sarebbe falso. */}
        {ancheCopie && nuovo.copie_collegate?.length ? (
          <> Sostituito anche nelle copie collegate: {testoCopie(nuovo.copie_collegate)}.</>
        ) : null}
        {!ancheCopie && vecchio.copie_collegate?.length ? (
          <>
            {" "}Le copie collegate ({testoCopie(vecchio.copie_collegate)}) restano con il file di
            prima.
          </>
        ) : null}
        <EsitoAccessibilita key={nuovo.id} documento={nuovo} />
      </>,
    );
  }

  async function riverifica(documento: DocumentoTrovato) {
    setErroreOperazione(null);
    setEsito(null);
    setInRiverifica(documento.id);
    try {
      const aggiornato = await riverificaDocumento(documento.id);
      const fonte = fonteLeggibile(aggiornato);
      setEsito(
        esitoRiverifica(
          documento.titolo,
          statoDi(documento),
          statoDi(aggiornato),
          documento.copie_collegate ?? [],
        ) + (fonte ? ` Fonte del giudizio: ${fonte.testo}.` : ""),
      );
      risorsa.ricarica();
    } catch (causa) {
      setErroreOperazione(
        causa instanceof ErroreApi
          ? causa.messaggioEsteso
          : "Riverifica non riuscita.",
      );
    } finally {
      setInRiverifica(null);
    }
  }

  return (
    <section className="pila">
      <header>
        <h1 className="titolo-pagina">Accessibilita' dei documenti</h1>
        <p className="sottotitolo">
          Documenti PDF che non soddisfano i requisiti di accessibilita' o
          ne attendono la verifica ufficiale.
        </p>
      </header>

      {risorsa.errore ? (
        <Avviso tono="critico" titolo="Elenco non disponibile">
          {risorsa.errore.messaggioEsteso}
        </Avviso>
      ) : null}
      {erroreOperazione ? (
        <Avviso tono="critico" titolo="Riverifica non riuscita">
          {erroreOperazione}
        </Avviso>
      ) : null}
      {esito ? <Avviso tono="positivo">{esito}</Avviso> : null}

      <div className="griglia-kpi">
        {CONTATORI.map((stato) => {
          const valore = conteggi[stato] ?? 0;
          // Il tono e' quello del chip, cosi' il contatore e la riga hanno lo
          // stesso colore. A zero ogni contatore e' neutro, tranne «da
          // correggere», che a zero e' una buona notizia e lo dice.
          const tono =
            valore > 0
              ? TONO_ACCESSIBILITA[stato]
              : stato === "non_garantita"
                ? "positivo"
                : "neutro";
          return (
            <div key={stato} className={`kpi kpi--${tono}`}>
              <div className="kpi__etichetta">{etichettaTitolo(stato)}</div>
              <div className="kpi__valore">{valore}</div>
            </div>
          );
        })}
      </div>

      {/* Le spiegazioni restano a un clic, chiuse: chi corregge documenti ogni
          giorno le ha gia' lette, e aperte spingevano l'elenco sotto la piega. */}
      <LegendaAccessibilita />

      <details className="legenda-compatta">
        <summary>Note sull'elenco</summary>
        <ul className="pila-stretta campo__aiuto">
          <li>
            · Il giudizio «{etichettaTitolo("verificata")}» e' rilasciato solo
            dal validatore ufficiale veraPDF; la colonna «Secondo chi» ne indica
            la fonte.
          </li>
          <li>
            · I documenti da correggere restano pubblicati. «Sostituisci» carica
            la nuova versione in bozza; l'indirizzo precedente resta attivo.
          </li>
          <li>· I formati JSON, CSV e XML non sono soggetti a controllo.</li>
          <li>
            · «Riverifica» (o «Riprova») ripete il controllo sul file in archivio.
          </li>
        </ul>
      </details>

      <Scheda
        titolo={`Documenti (${elenco?.totale ?? 0})`}
        descrizione="Filtra per stato, o cerca nel titolo del documento."
        azioni={
          <div className="riga">
            {STATI_FILTRABILI.map((stato) => (
              <Bottone
                key={stato}
                variante={statiScelti.includes(stato) ? "primario" : "contorno"}
                dimensione="piccolo"
                aria-pressed={statiScelti.includes(stato)}
                onClick={() => commuta(stato)}
                title={DESCRIZIONE_ACCESSIBILITA[stato]}
              >
                {ETICHETTA_ACCESSIBILITA[stato]}
              </Bottone>
            ))}
          </div>
        }
        fitto
      >
        <form
          className="riga"
          style={{ padding: "0.75rem 1.25rem" }}
          onSubmit={(evento) => {
            evento.preventDefault();
            setCercato(testo.trim());
          }}
        >
          <label className="campo" style={{ flex: "1 1 18rem" }}>
            <span className="campo__etichetta">Cerca nel titolo</span>
            <input
              className="campo__controllo"
              type="search"
              value={testo}
              onChange={(evento) => setTesto(evento.target.value)}
            />
          </label>
          <Bottone type="submit" variante="primario" dimensione="piccolo">
            Cerca
          </Bottone>
        </form>

        {risorsa.inCaricamento ? (
          <div style={{ padding: "1.25rem" }}>
            <Scheletro righe={5} />
          </div>
        ) : documenti.length === 0 ? (
          <StatoVuoto
            titolo={
              statiScelti.length === 0
                ? "Nessuno stato selezionato"
                : "Nessun documento da correggere"
            }
            testo={
              statiScelti.length === 0
                ? "Selezionare almeno uno stato per visualizzare i documenti."
                : `Nessun documento corrisponde ai filtri. In assenza del validatore ufficiale i documenti restano «${ETICHETTA_ACCESSIBILITA.presunta}».`
            }
            azione={
              <Link className="bottone bottone--contorno" to="/documenti">
                Vai a Documenti e cartelle
              </Link>
            }
          />
        ) : (
          <div className="tabella-scorrevole">
            <table className="tabella">
              <caption className="visually-hidden">
                Documenti raggruppati per obbligo, con il verdetto di
                accessibilita', chi lo ha espresso e gli errori rilevati.
              </caption>
              <thead>
                <tr>
                  <th scope="col">Documento</th>
                  <th scope="col">Accessibilita'</th>
                  <th scope="col">Secondo chi</th>
                  <th scope="col">Errori rilevati</th>
                  <th scope="col">Azioni</th>
                </tr>
              </thead>
              {gruppi.map((gruppo) => (
                <tbody key={gruppo.chiave || "senza-obbligo"}>
                  <tr>
                    <th scope="colgroup" colSpan={5} className="tabella__gruppo">
                      {gruppo.denominazione}
                    </th>
                  </tr>
                  {gruppo.documenti.map((documento) => {
                    const stato = statoDi(documento);
                    const mancanze = mancanzeDi(documento);
                    const fonte = fonteLeggibile(documento);
                    const sostituto = sostituiti.get(documento.id);
                    const archiviato = documento.pubblicazione?.stato === "archiviato";
                    return (
                      <tr key={documento.id}>
                        <th scope="row">
                          {documento.titolo}
                          <div className="testo-fioco">
                            <Chip tono="neutro" pallino={false}>
                              {documento.formato.toUpperCase()}
                            </Chip>{" "}
                            {documento.peso}
                          </div>
                        </th>
                        <td>
                          <ChipAccessibilita stato={stato} />
                          {sostituto ? (
                            <div>
                              <Chip tono="primario" pallino={false}>
                                sostituto in bozza
                              </Chip>
                            </div>
                          ) : null}
                          {documento.accessibilita_verificata_il ? (
                            <div className="campo__aiuto">
                              verificato il {documento.accessibilita_verificata_il}
                            </div>
                          ) : null}
                        </td>
                        <td>
                          {fonte ? (
                            <>
                              {fonte.testo}
                              {fonte.tecnica ? (
                                <div className="campo__aiuto">{fonte.tecnica}</div>
                              ) : null}
                            </>
                          ) : (
                            <span className="testo-fioco">fonte non registrata</span>
                          )}
                        </td>
                        <td>
                          {mancanze.length === 0 ? (
                            <span className="testo-fioco">
                              Nessun rilievo registrato.
                            </span>
                          ) : (
                            // `<details>` e' nativo: si apre da tastiera e lo
                            // screen reader ne annuncia lo stato.
                            <details className="legenda-compatta">
                              <summary>{testoErroriRilevati(mancanze.length)}</summary>
                              <ul className="pila-stretta">
                                {mancanze.map((voce) => (
                                  <li key={voce}>· {voce}</li>
                                ))}
                              </ul>
                            </details>
                          )}
                        </td>
                        <td>
                          <div className="riga">
                            <Bottone
                              variante="contorno"
                              dimensione="piccolo"
                              inCorso={inRiverifica === documento.id}
                              disabled={inRiverifica === documento.id}
                              onClick={() => void riverifica(documento)}
                            >
                              {inRiverifica === documento.id
                                ? "Controllo in corso…"
                                : stato === "non_verificata"
                                  ? "Riprova"
                                  : "Riverifica"}
                            </Bottone>
                            <Bottone
                              variante="fantasma"
                              dimensione="piccolo"
                              disabled={archiviato}
                              title={
                                archiviato
                                  ? "La pubblicazione e' archiviata: resta consultabile ma non si modifica."
                                  : undefined
                              }
                              aria-label={`Sostituisci «${documento.titolo}»`}
                              onClick={() => setDaSostituire(documento)}
                            >
                              Sostituisci
                            </Bottone>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              ))}
            </table>
          </div>
        )}
      </Scheda>

      {daSostituire ? (
        <ModaleCaricamento
          key={daSostituire.id}
          titolo={`Sostituisci «${daSostituire.titolo}»`}
          contesto={
            daSostituire.nodo ? (
              <ChipNodo
                {...partiPercorso(catene, daSostituire.nodo.id_nodo)}
                separatore={SEPARATORE_PERCORSO}
              />
            ) : null
          }
          titoloFisso={daSostituire.titolo}
          formatoAttuale={daSostituire.formato}
          copie={daSostituire.copie_collegate ?? []}
          conScadenza
          // La scadenza del vecchio non passa al sostituto: se era ancora
          // futura la si propone, perche' rifare un file per l'accessibilita' non
          // ne cambia la validita'.
          scadenzaIniziale={
            daSostituire.valido_fino_al && !leggiScadenzaFile(daSostituire.valido_fino_al).errore
              ? daSostituire.valido_fino_al
              : null
          }
          onInvia={(file, _titolo, { ancheCopie, validoFinoAl }) =>
            sostituisciDocumento(daSostituire.id, file, { ancheCopie, validoFinoAl })
          }
          onChiudi={() => setDaSostituire(null)}
          onCompletato={(nuovo, { ancheCopie }) => sostituito(daSostituire, nuovo, ancheCopie)}
        />
      ) : null}
    </section>
  );
}
